package com.trevorwiebe.timesheet

import com.trevorwiebe.timesheet.invoicing.presentation.InvoicingScreen
import com.trevorwiebe.timesheet.settlements.presentation.SettlementsScreen
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.WindowInsets
import androidx.compose.foundation.layout.asPaddingValues
import androidx.compose.foundation.layout.consumeWindowInsets
import androidx.compose.foundation.layout.displayCutout
import androidx.compose.foundation.layout.navigationBars
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.padding
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.adaptive.currentWindowAdaptiveInfo
import androidx.compose.material3.adaptive.navigationsuite.NavigationSuiteScaffoldDefaults
import androidx.compose.material3.adaptive.navigationsuite.NavigationSuiteType
import androidx.compose.runtime.Composable
import androidx.compose.runtime.CompositionLocalProvider
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.platform.LocalWindowInfo
import androidx.compose.ui.unit.DpSize
import androidx.compose.ui.unit.dp
import androidx.window.core.layout.WindowSizeClass
import androidx.navigation.NavHostController
import androidx.navigation.compose.NavHost
import androidx.navigation.compose.composable
import androidx.navigation.compose.currentBackStackEntryAsState
import androidx.navigation.compose.rememberNavController
import androidx.navigation.toRoute
import com.trevorwiebe.timesheet.core.domain.DeepLinkState
import com.trevorwiebe.timesheet.admin.presentation.AdminScreen
import com.trevorwiebe.timesheet.admin.carrier.presentation.CarriersScreen
import com.trevorwiebe.timesheet.admin.ownerOperator.presentation.OwnerOperatorsScreen
import com.trevorwiebe.timesheet.admin.customer.presentation.CustomersScreen
import com.trevorwiebe.timesheet.admin.employeeHours.presentation.EmployeeHoursScreen
import com.trevorwiebe.timesheet.admin.equipment.presentation.EquipmentScreen
import com.trevorwiebe.timesheet.admin.locations.presentation.LocationsScreen
import com.trevorwiebe.timesheet.admin.organization.presentation.OrganizationScreen
import com.trevorwiebe.timesheet.admin.loadrate.presentation.LoadRateScreen
import com.trevorwiebe.timesheet.admin.rate.presentation.RateScreen
import com.trevorwiebe.timesheet.admin.reporting.presentation.ReportingScreen
import com.trevorwiebe.timesheet.admin.usermanagement.presentation.UserManagementScreen
import com.trevorwiebe.timesheet.calendar.presentation.CalendarScreen
import com.trevorwiebe.timesheet.core.domain.model.UserRole
import com.trevorwiebe.timesheet.core.presentation.AppEvent
import com.trevorwiebe.timesheet.core.presentation.AppViewModel
import com.trevorwiebe.timesheet.core.presentation.AppBottomBar
import com.trevorwiebe.timesheet.core.presentation.AppSideNav
import com.trevorwiebe.timesheet.core.presentation.common.DebugBadge
import com.trevorwiebe.timesheet.core.presentation.common.LocalNavSuiteType
import com.trevorwiebe.timesheet.core.presentation.common.LocalWindowSizeClass
import com.trevorwiebe.timesheet.core.presentation.common.NativeDialog
import com.trevorwiebe.timesheet.core.presentation.common.isSideNavVisible
import com.trevorwiebe.timesheet.core.presentation.common.ResponsiveDefaults
import com.trevorwiebe.timesheet.core.presentation.mainScreenRoutes
import com.trevorwiebe.timesheet.di.Debug
import com.trevorwiebe.timesheet.loads.presentation.admin.CompletedLoadsScreen
import com.trevorwiebe.timesheet.loads.presentation.LoadsScreen
import com.trevorwiebe.timesheet.mileage.presentation.MileageScreen
import com.trevorwiebe.timesheet.more.presentation.MoreScreen
import com.trevorwiebe.timesheet.punch.presentation.PunchScreen
import com.trevorwiebe.timesheet.payPeriod.presentation.PayPeriodScreen
import com.trevorwiebe.timesheet.signin.presentation.auth.SignInScreen
import com.trevorwiebe.timesheet.theme.AppTheme
import org.koin.compose.viewmodel.koinViewModel

private fun deepLinkRoute(destination: String?): Route? = when (destination) {
    "equipment" -> Route.Equipment
    "loads" -> Route.Loads
    else -> null
}

// Secondary screens (Admin section, Pay Periods, Time Off) sit above the main
// tabs. When the side nav is used from one of them, pop them first — without
// saving — so a tab click's popUpTo(saveState) can't capture them into the
// tab's saved state and restore them later.
private fun NavHostController.popSecondaryScreens() {
    while (
        currentBackStackEntry?.destination?.route !in mainScreenRoutes &&
        previousBackStackEntry != null
    ) {
        popBackStack()
    }
}

private fun NavHostController.navigateToSignInClearingTabState() {
    navigate(Route.SignIn) {
        popUpTo(Route.Punch) { inclusive = true }
    }
    // Tab state (incl. ViewModels) saved by saveState/restoreState survives the pop;
    // clear it so the next user doesn't restore the previous user's screens.
    clearBackStack<Route.Loads>()
    clearBackStack<Route.Mileage>()
    clearBackStack<Route.More>()
}

@Composable
fun App() {
    AppTheme {

        val navController = rememberNavController()
        val currentBackStack by navController.currentBackStackEntryAsState()
        val pendingDestination by DeepLinkState.pendingDestination.collectAsState()

        LaunchedEffect(pendingDestination, currentBackStack) {
            val target = deepLinkRoute(pendingDestination)
            if (target != null) {
                val currentRoute = currentBackStack?.destination?.route
                if (currentRoute != null && !currentRoute.contains("SignIn")) {
                    DeepLinkState.consume()
                    if (target == Route.Loads) {
                        // Loads is a bottom-bar tab; navigate like a tab click so
                        // the bar selection stays consistent.
                        navController.navigate(target) {
                            popUpTo(Route.Punch) { saveState = true }
                            launchSingleTop = true
                            restoreState = true
                        }
                    } else {
                        navController.navigate(target)
                    }
                }
            }
        }

        val appViewModel: AppViewModel = koinViewModel()
        val appState by appViewModel.state.collectAsState()

        val cutout = WindowInsets.displayCutout.asPaddingValues()

        val currentRoute = currentBackStack?.destination?.route

        // The user document is only readable once signed in, so (re)load it
        // whenever a signed-in tab becomes current.
        LaunchedEffect(currentRoute) {
            if (currentRoute in mainScreenRoutes) {
                appViewModel.onEvent(AppEvent.OnRefreshUser)
            }
        }

        LaunchedEffect(appViewModel.onSignOutChannel) {
            appViewModel.onSignOutChannel.collect {
                navController.navigateToSignInClearingTabState()
            }
        }

        val adaptiveInfo = currentWindowAdaptiveInfo()
        // WindowSizeClass buckets sizes into the standard classes (600/840), so a custom
        // breakpoint must be checked against the real window size instead.
        val containerSize = LocalWindowInfo.current.containerSize
        val windowSize = with(LocalDensity.current) {
            DpSize(containerSize.width.toDp(), containerSize.height.toDp())
        }
        // Bar/rail/drawer purely by window size. The height check keeps
        // landscape phones on the bar.
        val sizeBasedType = when {
            windowSize.width >= ResponsiveDefaults.navDrawerMinWidth &&
                windowSize.height >= WindowSizeClass.HEIGHT_DP_MEDIUM_LOWER_BOUND.dp ->
                NavigationSuiteType.NavigationDrawer
            else -> NavigationSuiteScaffoldDefaults.calculateFromAdaptiveInfo(adaptiveInfo)
        }
        val onSignInScreen = currentRoute == null || currentRoute.contains("SignIn")
        val layoutType = when {
            onSignInScreen -> NavigationSuiteType.None
            // The bottom bar only appears on the main tabs; the side nav stays
            // visible on every signed-in screen (incl. Time Off/Admin) so that
            // using its entries doesn't dismiss it.
            sizeBasedType == NavigationSuiteType.NavigationBar ->
                if (currentRoute in mainScreenRoutes) NavigationSuiteType.NavigationBar
                else NavigationSuiteType.None
            else -> sizeBasedType
        }

        val onTabClick: (Route) -> Unit = { route ->
            navController.popSecondaryScreens()
            navController.navigate(route) {
                popUpTo(Route.Punch) {
                    saveState = true
                }
                launchSingleTop = true
                restoreState = true
            }
        }

        CompositionLocalProvider(
            LocalWindowSizeClass provides adaptiveInfo.windowSizeClass,
            LocalNavSuiteType provides layoutType,
        ) {
        Box(Modifier.fillMaxSize()) {
        Surface(color = MaterialTheme.colorScheme.background) {
        Row(Modifier.fillMaxSize()) {
            if (layoutType == NavigationSuiteType.NavigationRail ||
                layoutType == NavigationSuiteType.NavigationDrawer
            ) {
                AppSideNav(
                    expanded = layoutType == NavigationSuiteType.NavigationDrawer,
                    currentRoute = currentRoute,
                    isAdmin = appState.currentUser?.role == UserRole.ADMIN,
                    onTabClick = onTabClick,
                    onPayPeriodsClick = {
                        navController.popSecondaryScreens()
                        navController.navigate(Route.PayPeriod) { launchSingleTop = true }
                    },
                    onTimeOffClick = {
                        navController.popSecondaryScreens()
                        navController.navigate(Route.Calendar) { launchSingleTop = true }
                    },
                    onAdminClick = {
                        navController.popSecondaryScreens()
                        navController.navigate(Route.Admin) { launchSingleTop = true }
                    },
                    onInvoicingClick = {
                        navController.popSecondaryScreens()
                        navController.navigate(Route.Invoicing) { launchSingleTop = true }
                    },
                    onSettlementsClick = {
                        navController.popSecondaryScreens()
                        navController.navigate(Route.Settlements) { launchSingleTop = true }
                    },
                    onSignOutClick = {
                        appViewModel.onEvent(AppEvent.OnShowConfirmSignOutDialog(true))
                    },
                )
            }
            Column(Modifier.weight(1f).fillMaxHeight()) {
            Box(
                Modifier
                    .weight(1f)
                    // The bottom bar (a sibling below) already applies the system
                    // navigation-bar inset, so tell the screen content it's handled —
                    // otherwise each screen's Scaffold reserves it again, leaving a
                    // gap above the bar. Only consume it while the bar is shown.
                    .then(
                        if (layoutType == NavigationSuiteType.NavigationBar)
                            Modifier.consumeWindowInsets(WindowInsets.navigationBars)
                        else Modifier
                    )
            ) {
            NavHost(
                modifier = Modifier.padding(cutout),
                navController = navController,
                startDestination = Route.SignIn
            ){

                composable<Route.SignIn> {
                    SignInScreen(
                        onSignInSuccessful = {
                            val pending = deepLinkRoute(DeepLinkState.consume())
                            navController.navigate(Route.Punch) {
                                popUpTo(Route.SignIn) { inclusive = true }
                            }
                            if (pending != null) {
                                navController.navigate(pending)
                            }
                        }
                    )
                }

                composable<Route.Punch> {
                    PunchScreen()
                }

                composable<Route.Loads> {
                    LoadsScreen(
                        onNavigateToCompleted = { navController.navigate(Route.CompletedLoads) },
                    )
                }

                composable<Route.CompletedLoads> {
                    CompletedLoadsScreen(onBack = { navController.popBackStack() })
                }

                composable<Route.PayPeriod> {
                    PayPeriodScreen(
                        onBack = if (isSideNavVisible()) null else ({ navController.popBackStack() }),
                        onPayPeriodClick = { startTime, endTime, timeSheetId ->
                            navController.navigate(
                                Route.PayPeriodDetail(
                                    startTime,
                                    endTime,
                                    timeSheetId
                                )
                            )
                        },
                    )
                }

                composable<Route.PayPeriodDetail> { entry ->
                    val args = entry.toRoute<Route.PayPeriodDetail>()
                    PunchScreen(
                        startDate = args.startTime,
                        endDate = args.endTime,
                        timeSheetId = args.timeSheetId,
                        userId = args.userId,
                        isAdmin = args.adminEdit,
                        onBack = { navController.popBackStack() },
                    )
                }

                composable<Route.Calendar> {
                    CalendarScreen(
                        onBack = { navController.popBackStack() }
                    )
                }

                composable<Route.Mileage> {
                    MileageScreen()
                }

                composable<Route.More> {
                    MoreScreen(
                        onNavigateToPayPeriods = {
                            navController.navigate(Route.PayPeriod)
                        },
                        onNavigateToTimeOff = {
                            navController.navigate(Route.Calendar)
                        },
                        onNavigateToAdmin = {
                            navController.navigate(Route.Admin)
                        },
                        onNavigateToInvoicing = {
                            navController.navigate(Route.Invoicing)
                        },
                        onNavigateToSettlements = {
                            navController.navigate(Route.Settlements)
                        },
                        onSignOut = {
                            navController.navigateToSignInClearingTabState()
                        }
                    )
                }

                composable<Route.Admin> {
                    AdminScreen(
                        onNavigateToOrganization = { navController.navigate(Route.Organization) },
                        onNavigateToUserManagement = { navController.navigate(Route.UserManagement) },
                        onNavigateToRates = { navController.navigate(Route.Rates) },
                        onNavigateToLoadRates = { navController.navigate(Route.LoadRates) },
                        onNavigateToEquipment = { navController.navigate(Route.Equipment) },
                        onNavigateToLocations = { navController.navigate(Route.Locations) },
                        onNavigateToCustomers = { navController.navigate(Route.Customers) },
                        onNavigateToCarriers = { navController.navigate(Route.Carriers) },
                        onNavigateToOwnerOperators = { navController.navigate(Route.OwnerOperators) },
                        onNavigateToReporting = { navController.navigate(Route.Reporting) },
                        onNavigateToEmployeeHours = { navController.navigate(Route.EmployeeHours) },
                        onBack = { navController.popBackStack() }
                    )
                }

                composable<Route.EmployeeHours> {
                    EmployeeHoursScreen(
                        onEmployeeClick = { userId, employeeName ->
                            navController.navigate(Route.EmployeePayPeriods(userId, employeeName))
                        },
                        onBack = { navController.popBackStack() }
                    )
                }

                composable<Route.EmployeePayPeriods> { entry ->
                    val args = entry.toRoute<Route.EmployeePayPeriods>()
                    PayPeriodScreen(
                        userId = args.userId,
                        title = args.employeeName,
                        onBack = { navController.popBackStack() },
                        onPayPeriodClick = { startTime, endTime, timeSheetId ->
                            navController.navigate(
                                Route.PayPeriodDetail(
                                    startTime = startTime,
                                    endTime = endTime,
                                    timeSheetId = timeSheetId,
                                    userId = args.userId,
                                    adminEdit = true,
                                )
                            )
                        },
                    )
                }

                composable<Route.Organization> {
                    OrganizationScreen(onBack = { navController.popBackStack() })
                }

                composable<Route.UserManagement> {
                    UserManagementScreen(onBack = { navController.popBackStack() })
                }

                composable<Route.Rates> {
                    RateScreen(onBack = { navController.popBackStack() })
                }

                composable<Route.LoadRates> {
                    LoadRateScreen(onBack = { navController.popBackStack() })
                }

                composable<Route.Equipment> {
                    EquipmentScreen(onBack = { navController.popBackStack() })
                }

                composable<Route.Locations> {
                    LocationsScreen(onBack = { navController.popBackStack() })
                }

                composable<Route.Customers> {
                    CustomersScreen(onBack = { navController.popBackStack() })
                }

                composable<Route.Carriers> {
                    CarriersScreen(onBack = { navController.popBackStack() })
                }

                composable<Route.OwnerOperators> {
                    OwnerOperatorsScreen(onBack = { navController.popBackStack() })
                }

                composable<Route.Reporting> {
                    ReportingScreen(onBack = { navController.popBackStack() })
                }

                composable<Route.Invoicing> {
                    InvoicingScreen(onBack = { navController.popBackStack() })
                }
                composable<Route.Settlements> {
                    SettlementsScreen(onBack = { navController.popBackStack() })
                }
            }
            }
            if (layoutType == NavigationSuiteType.NavigationBar) {
                AppBottomBar(
                    currentRoute = currentRoute,
                    onTabClick = onTabClick,
                )
            }
            }
        }
        }

        NativeDialog(
            visible = appState.confirmSignOutDialog,
            title = "Sign Out",
            message = "Are you sure you want to sign out?",
            onDismiss = { appViewModel.onEvent(AppEvent.OnShowConfirmSignOutDialog(false)) },
            dismissText = "Cancel",
            onConfirm = { appViewModel.onEvent(AppEvent.OnSignOut) },
            confirmText = "Sign Out"
        )

        if (Debug.isDebug) {
            DebugBadge(Modifier.align(Alignment.TopCenter))
        }
        }
        }
    }
}